import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withProps,
} from '@ngrx/signals';
import {
  withEntities,
  entityConfig,
  EntityId,
  setEntity,
  updateEntity,
  removeEntity,
} from '@ngrx/signals/entities';
import {
  withCallStatus,
  withEntitiesLoadingCall,
  withCalls,
  withEntitiesCalls,
  callConfig,
  entityCallConfig,
  withLogger,
  withEntitiesLocalSort,
  withEntitiesMultiSelection,
  withEntitiesLocalPagination,
} from '@ngrx-traits/signals';
import {
  CaseType,
  CaseTypeView,
  toPatchOperations,
} from '../models/case-type.model';
import { CaseTypeService } from '../data/case-type.service';
import { map, filter, throwError } from 'rxjs';
import { ToastService } from '@core/toast';
import { ConfirmDeleteDialogService } from '@core/confirmation';
import { EnumStore } from '@app/features/options/enum';
import {
  DamageCategory,
  GdvDamageCategory,
  GdvDamageKind,
} from 'src/app/models/enum-types.model';

const caseTypeConfig = entityConfig({
  entity: type<CaseType>(),
  collection: 'caseType',
  selectId: (caseType: CaseType) => caseType.id,
});

export const CaseTypeListStore = signalStore(
  withEntities(caseTypeConfig),
  withCallStatus({ collection: caseTypeConfig.collection }),
  withEntitiesLocalSort(
    { entity: type<CaseType>(), collection: caseTypeConfig.collection },
    {
      defaultSort: { field: 'name', direction: 'asc' },
    }
  ),
  withEntitiesMultiSelection({
    entity: type<CaseType>(),
    collection: caseTypeConfig.collection,
  }),
  withEntitiesLocalPagination(
    { entity: type<CaseType>(), collection: caseTypeConfig.collection },
    {
      pageSize: 20,
    }
  ),
  withLogger({
    name: 'CaseTypeListStore',
    showDiff: true,
  }),
  withProps(() => ({
    dialogService: inject(ConfirmDeleteDialogService),
    caseTypeService: inject(CaseTypeService),
    _toastService: inject(ToastService),
    enumStore: inject(EnumStore),
  })),
  withComputed(({ ...store }) => ({
    damageCategoryEnums: computed<DamageCategory[]>(() =>
      store.enumStore.enumForKey<DamageCategory>('DamageCategory')
    ),
    gdvDamageCategoryEnums: computed<GdvDamageCategory[]>(() =>
      store.enumStore.enumForKey<GdvDamageCategory>('GdvDamageCategory')
    ),
    gdvDamageKindEnums: computed<GdvDamageKind[]>(() =>
      store.enumStore.enumForKey<GdvDamageKind>('GdvDamageKind')
    ),
  })),
  withEntitiesLoadingCall({
    collection: caseTypeConfig.collection,
    fetchEntities: ({ caseTypeService }) => {
      return caseTypeService.loadList();
    },
  }),
  withComputed(({ caseTypeIds }) => ({
    isEmpty: computed(() => caseTypeIds()?.length === 0),
    totalCount: computed(() => caseTypeIds()?.length || 0),
  })),
  withEntitiesCalls(caseTypeConfig, ({ caseTypeService, ...store }) => ({
    updateCaseType: entityCallConfig({
      paramsSelectId: (update) => String(update.id),
      call: (update: { id: EntityId; changes: Partial<CaseType> }) =>
        caseTypeService
          .update(String(update.id), toPatchOperations(update.changes))
          .pipe(map(() => update)),
      mapError: () => 'Fehler beim Aktualisieren der Vorgangsart',
      onSuccess: (update) => {
        patchState(
          store,
          updateEntity(
            {
              id: update.id,
              changes: update.changes,
            },
            caseTypeConfig
          )
        );
      },
    }),
    deleteCaseType: entityCallConfig({
      call: (id: EntityId) =>
        caseTypeService.delete(String(id)).pipe(map(() => id)),
      mapError: () => 'Fehler beim Löschen der Vorgangsart',
      onSuccess: (id) => {
        patchState(store, removeEntity(id, caseTypeConfig));
        store._toastService.success('Vorgangsart gelöscht', '');
      },
    }),
  })),
  withComputed(
    ({
      caseTypeEntitiesCurrentPage,
      damageCategoryEnums,
      gdvDamageCategoryEnums,
      gdvDamageKindEnums,
    }) => ({
      caseTypeViews: computed<CaseTypeView[]>(() => {
        const entities = caseTypeEntitiesCurrentPage().entities ?? [];
        const damageCategories = damageCategoryEnums() ?? [];
        const gdvDamageCategories = gdvDamageCategoryEnums() ?? [];
        const gdvDamageKinds = gdvDamageKindEnums() ?? [];

        return entities
          .filter((e): e is CaseType => !!e)
          .map((entity) => ({
            ...entity,
            damageCategoryDescription: damageCategories.find(
              (d) => d.id === entity.damageCategoryId
            )?.description,
            gdvDamageCategoryDescription: gdvDamageCategories.find(
              (d) => d.id === entity.gdvDamageCategoryId
            )?.description,
            gdvDamageKindDescription: gdvDamageKinds.find(
              (g) => g.id === entity.gdvDamageKindId
            )?.description,
          }));
      }),
    })
  ),
  withCalls(
    ({
      dialogService,
      caseTypeService,
      deleteCaseType,
      _toastService,
      ...state
    }) => ({
      createCaseType: callConfig({
        call: (caseType: Partial<CaseType>) => caseTypeService.create(caseType),
        mapError: () => 'Fehler beim Erstellen der Vorgangsart',
        onSuccess: (caseType) => {
          patchState(
            state,
            setEntity(caseType, {
              collection: caseTypeConfig.collection,
            })
          );
          _toastService.success('Vorgangsart angelegt', '');
        },
      }),
      requestDeleteDialog: callConfig({
        call: (caseTypeId: EntityId) =>
          dialogService
            .confirmDelete(
              'Sind Sie sicher, dass Sie diese Vorgangsart löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'
            )
            .pipe(
              filter((confirmed) => confirmed),
              map(() => caseTypeId)
            ),
        onSuccess: (caseTypeId) => deleteCaseType(caseTypeId),
      }),
      requestDeleteSelectedDialog: callConfig({
        call: () => {
          const selectedIds = state.caseTypeIdsSelected();
          if (selectedIds.length === 0) {
            return throwError(() => new Error('No case types selected'));
          }
          return dialogService.confirmDelete().pipe(
            filter((confirmed) => confirmed),
            map(() => selectedIds.map((x) => parseInt(x.toString())))
          );
        },
        onSuccess: (selectedIds) => {
          selectedIds.forEach((id: EntityId) => deleteCaseType(id));
          patchState(state, { caseTypeIdsSelectedMap: {} });
        },
      }),
    })
  )
);
